import React from 'react';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash-es';

import { Field, Button, Sheet, useSheetDismiss } from '@/nova/components';
import { Icon } from '@/nova/components/Icon/Icon';
import { affiliateConsoleNext, createAffiliate, getAffiliateError, setAffiliateStatus, updateAffiliate } from '../../api';
import type { Affiliate, AffiliateInvite, CommissionPlan } from '../../types';
import { isAffiliateActive } from '../../mapping';
import { isOpenableHttpUrl } from '../../url-safety';
import { useOpenConsole } from '../../hooks/useOpenConsole';
import { SitesSection } from '../SitesSection/SitesSection';
import { CommissionSection } from '../CommissionSection/CommissionSection';
import { FormCol, FieldGroup, GroupLabel, StatusRow, SegChips, SegChip, Helper } from '../formControls.styles';
import {
  InvitePanel,
  InviteTitle,
  InviteBody,
  TokenBox,
  TokenText,
  SheetHeaderLeft,
  SheetHeaderTitle,
  SheetSsoBtn,
} from './AffiliateForm.styles';

export type AffiliateFormProps = {
  casinoInstanceId: string;
  /** Present → edit mode (profile, status toggle, site-IDs); absent → create mode. */
  affiliate?: Affiliate;
  /** Plans already in use across the roster, offered as "Same deal as another affiliate" in the commission section. */
  reusablePlans?: CommissionPlan[];
  onClose: () => void;
  /** Called after any successful create / update / status change so the roster refetches. */
  onSaved: () => void;
  onToast: (message: string) => void;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const trimOrUndefined = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/**
 * Add / edit an affiliate. Create collects email + profile and, when no password is
 * set, surfaces the returned one-time invite token to hand to the partner. Edit
 * updates the profile, toggles active/paused, and manages the affiliate's site-IDs.
 */
export const AffiliateForm = ({
  casinoInstanceId,
  affiliate,
  reusablePlans,
  onClose,
  onSaved,
  onToast,
}: AffiliateFormProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle, dismiss } = useSheetDismiss(onClose);
  const isEdit = affiliate !== undefined;

  // SSO into the Affiliates.bet console via the shared hook (opens in a new tab; errors toast).
  // In edit mode we pass that affiliate's console page (`affiliateConsoleNext`, built from the SHORT
  // number affiliate_no, which the console route resolves by, not the UUID) so `/access` deep-links
  // straight to it; in create mode there is no affiliate yet, so it lands on the brand console root.
  const openConsole = useOpenConsole(casinoInstanceId);
  const onOpenConsole = React.useCallback(() => {
    const affiliateNo = affiliate?.affiliate_no;
    openConsole
      .open(isNil(affiliateNo) ? undefined : affiliateConsoleNext(affiliateNo))
      .catch((err: unknown) => onToast(getAffiliateError(err).message));
  }, [openConsole, affiliate, onToast]);

  const [email, setEmail] = React.useState(affiliate?.email ?? '');
  const [displayName, setDisplayName] = React.useState(affiliate?.display_name ?? '');
  const [company, setCompany] = React.useState(affiliate?.company ?? '');
  const [country, setCountry] = React.useState(affiliate?.country ?? '');
  const [phone, setPhone] = React.useState(affiliate?.phone ?? '');
  const [telegram, setTelegram] = React.useState(affiliate?.telegram ?? '');
  const [teams, setTeams] = React.useState(affiliate?.teams ?? '');
  const [password, setPassword] = React.useState('');

  const [invite, setInvite] = React.useState<AffiliateInvite | null>(null);
  const [saving, setSaving] = React.useState(false);

  const [active, setActive] = React.useState<boolean>(affiliate ? isAffiliateActive(affiliate) : true);
  const [statusSaving, setStatusSaving] = React.useState(false);

  const emailValid = EMAIL_RE.test(email.trim());
  const canSubmit = (isEdit || emailValid) && !saving;

  const onSubmit = React.useCallback(async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      if (isEdit && affiliate) {
        await updateAffiliate(casinoInstanceId, affiliate.id, {
          display_name: trimOrUndefined(displayName),
          company: trimOrUndefined(company),
          country: trimOrUndefined(country),
          phone: trimOrUndefined(phone),
          telegram: trimOrUndefined(telegram),
          teams: trimOrUndefined(teams),
        });
        onToast(t('Affiliate updated'));
        onSaved();
        dismiss();
        return;
      }
      const result = await createAffiliate(casinoInstanceId, {
        email: email.trim(),
        display_name: trimOrUndefined(displayName),
        company: trimOrUndefined(company),
        country: trimOrUndefined(country),
        phone: trimOrUndefined(phone),
        telegram: trimOrUndefined(telegram),
        teams: trimOrUndefined(teams),
        password: trimOrUndefined(password),
      });
      onSaved();
      if (result.invite?.token) {
        // Keep the sheet open to reveal the one-time invite token.
        setInvite(result.invite);
      } else {
        onToast(t('Affiliate created'));
        dismiss();
      }
    } catch (err: unknown) {
      onToast(getAffiliateError(err).message);
    } finally {
      setSaving(false);
    }
  }, [
    canSubmit,
    isEdit,
    affiliate,
    casinoInstanceId,
    displayName,
    company,
    country,
    phone,
    telegram,
    teams,
    email,
    password,
    onToast,
    onSaved,
    dismiss,
    t,
  ]);

  const onSetStatus = React.useCallback(
    async (next: boolean) => {
      if (!affiliate || statusSaving || next === active) return;
      setStatusSaving(true);
      const previous = active;
      setActive(next);
      try {
        await setAffiliateStatus(casinoInstanceId, affiliate.id, next ? 'active' : 'inactive');
        onToast(next ? t('Affiliate activated') : t('Affiliate paused'));
        onSaved();
      } catch (err: unknown) {
        setActive(previous);
        onToast(getAffiliateError(err).message);
      } finally {
        setStatusSaving(false);
      }
    },
    [affiliate, statusSaving, active, casinoInstanceId, onToast, onSaved, t],
  );

  // The backend-composed registration URL, when present (vendor `link`, or `invite_url` if the
  // proxy renamed it), scheme-guarded. Absent → we hand over the raw one-time token as before.
  const inviteUrl = React.useMemo(() => {
    const candidate = invite?.link ?? invite?.invite_url;
    return candidate && isOpenableHttpUrl(candidate) ? candidate : undefined;
  }, [invite]);

  // Copy the real link when we have one, else the bare token (the fallback path).
  const onCopyInvite = React.useCallback(() => {
    const value = inviteUrl ?? invite?.token;
    if (!value) return;
    void navigator.clipboard?.writeText(value).then(
      () => onToast(t('Invite link copied')),
      () => onToast(t('Could not copy')),
    );
  }, [inviteUrl, invite, onToast, t]);

  // Open the registration link in a new tab (only offered when we have a real URL). `noopener`
  // severs the reverse-tabnabbing handle; the scheme was already validated in `inviteUrl`.
  const onOpenInvite = React.useCallback(() => {
    if (inviteUrl) window.open(inviteUrl, '_blank', 'noopener,noreferrer');
  }, [inviteUrl]);

  const footer = invite ? (
    <Button label={t('Done')} variant="primary" fullWidth onClick={dismiss} />
  ) : (
    <Button
      label={isEdit ? t('Save changes') : t('Add affiliate')}
      variant="primary"
      fullWidth
      onClick={onSubmit}
      disabled={!canSubmit}
    />
  );

  const editName = affiliate?.display_name?.trim() || affiliate?.email || '';
  // Edit mode gets a custom header (gradient title + SSO icon) via the Sheet's `header` slot;
  // create mode falls back to the plain `title`. The Sheet still owns the close button.
  const header = isEdit ? (
    <SheetHeaderLeft>
      <SheetHeaderTitle text="Edit {{name}}" placeholders={{ name: editName }} tag="span" />
      <SheetSsoBtn type="button" aria-label={t('Advanced Settings')} onClick={onOpenConsole}>
        <Icon name="external-link" size={16} />
      </SheetSsoBtn>
    </SheetHeaderLeft>
  ) : undefined;

  return (
    <Sheet
      title={isEdit ? t('Edit {{name}}') : t('Add affiliate')}
      titlePlaceholders={isEdit ? { name: editName } : undefined}
      header={header}
      ariaLabel={isEdit ? t('Edit affiliate') : t('Add affiliate')}
      onClose={onClose}
      closeHandle={closeHandle}
      busy={saving}
      animateResize
      footer={footer}
    >
      {invite ? (
        <InvitePanel>
          <InviteTitle text="Affiliate created" tag="span" />
          <InviteBody
            text="Share this one-time invite link with the partner so they can set their own password. It is shown only once."
            tag="span"
          />
          <TokenBox>
            <TokenText>{inviteUrl ?? invite.token}</TokenText>
            <Button label={t('Copy')} variant="outline" icon="copy" onClick={onCopyInvite} />
          </TokenBox>
          {inviteUrl ? (
            <Button label={t('Open link')} variant="outline" icon="external-link" onClick={onOpenInvite} />
          ) : null}
        </InvitePanel>
      ) : (
        <FormCol>
          <Field
            label={t('Email')}
            value={email}
            placeholder="promo@partner.com"
            disabled={isEdit}
            onChange={setEmail}
          />
          <Field label={t('Display name')} value={displayName} placeholder="Partner Promo" onChange={setDisplayName} />
          <Field label={t('Company (optional)')} value={company} placeholder={t('Company name')} onChange={setCompany} />
          <Field label={t('Country (optional)')} value={country} placeholder="MX" onChange={setCountry} />
          <Field label={t('Phone (optional)')} value={phone} placeholder="+1 555 0100" onChange={setPhone} />
          <Field label={t('Telegram (optional)')} value={telegram} placeholder="@handle" onChange={setTelegram} />
          <Field label={t('Teams (optional)')} value={teams} placeholder="handle" onChange={setTeams} />

          {isEdit && affiliate ? (
            <>
              <StatusRow>
                <GroupLabel text="Status" tag="span" />
                <SegChips>
                  <SegChip type="button" $selected={active} aria-pressed={active} onClick={() => onSetStatus(true)}>
                    {t('Active')}
                  </SegChip>
                  <SegChip type="button" $selected={!active} aria-pressed={!active} onClick={() => onSetStatus(false)}>
                    {t('Paused')}
                  </SegChip>
                </SegChips>
              </StatusRow>
              <SitesSection casinoInstanceId={casinoInstanceId} affiliateId={affiliate.id} onToast={onToast} />
              <CommissionSection
                casinoInstanceId={casinoInstanceId}
                affiliateId={affiliate.id}
                reusablePlans={reusablePlans}
                onToast={onToast}
                onChanged={onSaved}
              />
            </>
          ) : (
            <FieldGroup>
              <Field
                label={t('Password (optional)')}
                value={password}
                placeholder={t('Leave blank to send an invite link')}
                onChange={setPassword}
              />
              <Helper text="With no password we return a one-time invite link for the partner to set their own." tag="span" />
            </FieldGroup>
          )}
        </FormCol>
      )}
    </Sheet>
  );
};

export default AffiliateForm;
